import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Form, InputNumber, Modal, message } from 'antd';
import {
	getCurrency,
	isFiniteNumber,
	nullSafeGet,
	nullSafeGetOrElse,
	roundOffToDecimal,
} from '../../utils/DataAccessUtils';
import { useForm } from 'antd/lib/form/Form';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { ppslAdjustForSupplier } from '../../thunks/parts_per_stock_locations_thunks';
import { partCatalogsRestCrudThunksForSupplier } from '../../thunks/part_catalogs_thunks';
import TextArea from 'antd/lib/input/TextArea';
import PartEquipmentPricePicker from '../common/PartEquipmentPricePicker';

const FORM_NAME = 'ppsl-adjustment-form';

const PPSLAdjustmentModal: FC<any> = ({
	ppsl,
	onCancel,
	onSuccess,
	currentUser,
	match,
	ppslAdjust,
	fetchPartsCatalog,
}): React.ReactElement => {
	const partsPerStockLocationId = useMemo(() => match.params.id, [match.params.id]);

	const [form] = useForm();

	const [saving, setSaving] = useState(false);

	const adjustOnHandQuantity = Form.useWatch('adjustOnHandQuantity', form);
	const adjustTotalStockedPartsCost = Form.useWatch('adjustTotalStockedPartsCost', form);

	const { setFieldsValue } = form;

	useEffect(() => {
		const changedValue = parseInt(adjustOnHandQuantity);
		!isNaN(changedValue) && setFieldsValue({ adjustOnHandQuantity: changedValue });
	}, [adjustOnHandQuantity, setFieldsValue]);

	const userCurrencyId = useMemo(
		() => nullSafeGet('id', getCurrency({ currentUser })),
		[currentUser]
	);

	const [selectedCost, setSelectedCost] = useState(null);
	const [catalogCurrencyId, setCatalogCurrencyId] = useState(userCurrencyId);

	const partId = useMemo(() => nullSafeGet('partId', ppsl), [ppsl]);
	const partDefaultVendorId = useMemo(() => nullSafeGet('part.defaultVendorId', ppsl), [ppsl]);
	const partCost = useMemo(() => nullSafeGet('part.cost', ppsl), [ppsl]);
	const hasPartCost = partCost !== null && partCost !== undefined && partCost !== '';

	const currentOnHandQty = useMemo(
		() => parseFloat(nullSafeGetOrElse('onHandQuantity', ppsl, 0)) || 0,
		[ppsl]
	);
	const currentTotalCost = useMemo(
		() => parseFloat(nullSafeGetOrElse('totalStockedPartsCost', ppsl, 0)) || 0,
		[ppsl]
	);

	const qtyIsNumber = isFiniteNumber(adjustOnHandQuantity);
	const costIsNumber = isFiniteNumber(adjustTotalStockedPartsCost);

	const newOnHandQuantity = qtyIsNumber
		? currentOnHandQty + adjustOnHandQuantity
		: currentOnHandQty;
	const newTotalStockedPartsCost = costIsNumber
		? currentTotalCost + adjustTotalStockedPartsCost
		: currentTotalCost;
	const newWeightedAvgCost =
		newOnHandQuantity > 0 ? newTotalStockedPartsCost / newOnHandQuantity : 0;

	const onPriceSelect = useCallback(
		(unitPrice) => {
			setSelectedCost(unitPrice);
			const quantityValue = form.getFieldValue('adjustOnHandQuantity');
			if (isFiniteNumber(quantityValue)) {
				setFieldsValue({
					adjustTotalStockedPartsCost: roundOffToDecimal(unitPrice * quantityValue),
				});
			}
		},
		[form, setFieldsValue]
	);

	const onCatalogLoad = useCallback(
		(_catalog, nextCatalogCurrencyId) => {
			setCatalogCurrencyId(nextCatalogCurrencyId || userCurrencyId);
		},
		[userCurrencyId]
	);

	// Keep adjustTotalStockedPartsCost in sync with (selectedCost * adjustOnHandQuantity)
	// once a price is picked and quantity is entered.
	useEffect(() => {
		if (selectedCost !== null && selectedCost !== undefined && qtyIsNumber) {
			const projectedTotal = roundOffToDecimal(selectedCost * adjustOnHandQuantity);
			if (projectedTotal !== roundOffToDecimal(adjustTotalStockedPartsCost)) {
				setFieldsValue({ adjustTotalStockedPartsCost: projectedTotal });
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [adjustOnHandQuantity, selectedCost]);

	// Clear the picker selection when the user manually edits total cost away from
	// selectedCost * adjustOnHandQuantity.
	useEffect(() => {
		if (selectedCost === null || selectedCost === undefined) return;
		if (!qtyIsNumber || !costIsNumber) return;
		const projectedTotal = roundOffToDecimal(selectedCost * adjustOnHandQuantity);
		if (projectedTotal !== roundOffToDecimal(adjustTotalStockedPartsCost)) {
			setSelectedCost(null);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [adjustTotalStockedPartsCost]);

	const onSubmit = useCallback(
		(values) => {
			setSaving(true);
			ppslAdjust(partsPerStockLocationId, {
				...values,
				adjustTotalStockedPartsCost: `${values.adjustTotalStockedPartsCost}`,
			})
				.then((res) => {
					message.success('Adjustment added successfully!');
					onSuccess && onSuccess(res);
				})
				.catch((err) => message.error(err))
				.finally(() => setSaving(false));
		},
		[onSuccess, partsPerStockLocationId, ppslAdjust]
	);

	const renderCost = useCallback(
		(value) => getCurrency({ currencyId: catalogCurrencyId }).format(value),
		[catalogCurrencyId]
	);

	return (
		<Modal
			visible={true}
			width={600}
			title={`${nullSafeGetOrElse('part.name', ppsl, '')} Adjustment`}
			closable
			onCancel={onCancel}
			forceRender
			footer={[
				<Button onClick={onCancel} size="large">
					Cancel
				</Button>,
				<Button
					type="primary"
					size="large"
					style={{ marginLeft: '16px' }}
					key="submit"
					htmlType="submit"
					form={FORM_NAME}
					loading={saving}
				>
					Add Adjustment
				</Button>,
			]}
		>
			<Form form={form} id={FORM_NAME} layout="vertical" requiredMark={false} onFinish={onSubmit}>
				<Form.Item
					name="adjustOnHandQuantity"
					label="Add Quantity"
					extra={qtyIsNumber ? `Total quantity after adjustment: ${newOnHandQuantity}` : null}
					rules={[
						{
							required: true,
							message: `Please enter quantity`,
						},
						{
							type: 'number',
							message: 'Please enter a valid number',
						},
					]}
				>
					<InputNumber style={{ width: '100%' }} step={1} />
				</Form.Item>
				<Form.Item
					name="adjustTotalStockedPartsCost"
					label={
						<div className="flex flex-row items-center">
							Add Total Cost
							{partId && (partDefaultVendorId || hasPartCost) ? (
								<PartEquipmentPricePicker
									entityId={partId}
									entityName="partId"
									vendorId={partDefaultVendorId}
									fetchMethod={fetchPartsCatalog}
									fallbackCurrencyId={userCurrencyId}
									value={selectedCost}
									onChange={onPriceSelect}
									onCatalogLoad={onCatalogLoad}
									entityCost={partCost}
									defaultToListPrice
								/>
							) : null}
						</div>
					}
					extra={
						qtyIsNumber && costIsNumber ? (
							<div className="flex flex-col">
								<span>{`Total cost after adjustment: ${renderCost(
									newTotalStockedPartsCost
								)}`}</span>
								<span>{`Weighted avg. cost after adjustment: ${renderCost(
									newWeightedAvgCost
								)}`}</span>
							</div>
						) : null
					}
					rules={[
						{
							required: true,
							message: `Please enter total cost`,
						},
						{
							type: 'number',
							message: 'Please enter a valid number',
						},
					]}
				>
					<InputNumber style={{ width: '100%' }} addonAfter={catalogCurrencyId} />
				</Form.Item>
				<Form.Item
					name="notes"
					label="Notes"
					rules={[
						{
							required: true,
							message: `Please enter notes`,
						},
					]}
				>
					<TextArea style={{ width: '100%' }} />
				</Form.Item>
			</Form>
		</Modal>
	);
};

const mapStateToProps = (state) => ({
	currentUser: state.session.currentUser,
});

const mapDispatchToProps = (dispatch) => ({
	ppslAdjust: (id, data) => dispatch(ppslAdjustForSupplier(id, data)),
	fetchPartsCatalog: (params, targetCollectionName) =>
		dispatch(partCatalogsRestCrudThunksForSupplier.read(params, targetCollectionName)),
});

export default withRouter<any, any>(
	connect(mapStateToProps, mapDispatchToProps)(PPSLAdjustmentModal)
);
